import AppModel from 'Models/AppModel';
import { formatStringByObject } from 'Root/helpers/tools';

export function getDefaultSeoData() {
  const urn = typeof window === 'undefined'
    ? this.$ssrContext.url
    : window.location.pathname;

  const title = AppModel.APP_TITLE;

  return {
    title,
    meta: {
      description: null,
      'og:type': 'website',
      'og:title': title,
      'og:url': `${AppModel.URL}${urn}`,
      'og:image': '/images/og-logo.jpg',
      'og:description': null,
    },
  };
}

export function getBreadcrumbsJsonLd(breadcrumbs) {
  const itemListElement = [];
  breadcrumbs.forEach((breadcrumb, index) => {
    itemListElement.push({
      '@type': 'ListItem',
      item: `${AppModel.URL}${breadcrumb.href}`,
      name: breadcrumb.title,
      position: index + 1,
    });
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement,
  };
}

export function getProductJsonLd(product) {
  const {
    name,
    description,
    image,
    url,
    brand,
    offer,
    rating,
  } = product;

  const productMarkupObject = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    brand: {
      '@type': 'Brand',
      name: brand ?? 'SkyCoach',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: rating?.value ?? 5,
      reviewCount: rating?.count >= 100 ? rating.count : Math.floor(Math.random() * (150 - 100)) + 100,
    },
    ...(name && { name }),
    ...(description && { description }),
    ...(url && { url: AppModel.URL + url }),
    ...(image && { image: AppModel.URL + image }),
  };

  if (offer) {
    const {
      type,
      price,
      lowPrice,
      highPrice,
      priceCurrency,
      offerCount,
      inStock,
    } = offer;

    productMarkupObject.offers = {
      '@type': type ?? 'Offer',
      seller: {
        '@type': 'Organization',
        name: 'SkyCoach',
      },
      availability: `https://schema.org/${inStock ? 'InStock' : 'OutOfStock'}`,
      ...(url && { url: AppModel.URL + url }),
      ...(priceCurrency && { priceCurrency }),
      ...(price && { price: String(price) }),
      ...(lowPrice && { lowPrice }),
      ...(highPrice && { highPrice }),
      ...(offerCount && { offerCount }),
    };
  }

  return productMarkupObject;
}

export function getAuthorJsonLd(author) {
  const jsonLdData = {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    author: {
      '@type': 'Person',
      name: author?.name || 'Skycoach',
      image: author?.avatar,
      description: author?.description,
      jobTitle: 'Author',
    },
  };

  if (author?.socials) {
    jsonLdData.author.sameAs = Object.values(author.socials);
  }

  return jsonLdData;
}

export function getArticleJsonLd(article) {
  const jsonLdData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    datePublished: article.published_at.split('T')[0],
    dateModified: article.updated_at.split('T')[0],
    headline: article.title,
    description: article.description,
    articleSection: article.category.title,
    speakable: {
      '@type': 'SpeakableSpecification',
      xpath: [
        '/html/head/title',
        '/html/head/meta[@name=\'description\']/@content',
      ],
    },
    author: {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: article.blog_author?.name || 'Skycoach',
      image: article.blog_author?.avatar,
      description: article.blog_author?.description,
      jobTitle: 'Author',
    },
    publisher: {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Skycoach',
      logo: {
        '@type': 'ImageObject',
        url: 'https://skycoach.gg/images/logos/skycoach.svg',
        width: '198px',
        height: '46px',
      },
    },
    mainEntityOfPage: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': article.canonicalUrl,
    },
  };

  if (article?.images) {
    jsonLdData.image = {
      '@type': 'ImageObject',
      url: `${AppModel.URL}/storage${article.images['900x700']}`,
      width: '900',
      height: '700',
    };
  }

  if (article?.blog_author?.socials) {
    jsonLdData.author.sameAs = Object.values(article.blog_author.socials);
  }

  return jsonLdData;
}

export function getProductTitleAndDescription(product) {
  let title = null;
  let description = null;

  const masterTagRoot = product?.master_tag_root || null;

  if (masterTagRoot && masterTagRoot.seo_product_title_template) {
    title = formatStringByObject(masterTagRoot.seo_product_title_template, { product: product.title });
  } else {
    title = product?.seo_title || product?.title;
  }

  if (masterTagRoot && masterTagRoot.seo_product_description_template) {
    description = formatStringByObject(masterTagRoot.seo_product_description_template, { product: product.title });
  } else {
    description = product?.seo_meta_description;
  }

  return {
    title,
    description,
  };
}

export function getFilenameByNamespace(namespace) {
  return `${namespace}.seo-text.html`;
}

export function decorateTables(rootElement) {
  rootElement.classList.add('description-table-container');

  rootElement.querySelectorAll('table').forEach((table) => {
    const wrapper = document.createElement('div');
    table.before(wrapper);
    wrapper.append(table);
    // Добавлем тень
    const shadow = document.createElement('div');
    const container = document.createElement('div');
    const intersector = document.createElement('div');
    wrapper.style.position = 'relative';
    shadow.classList.add('product-description-table-wrapper--shadow');

    container.style.overflowX = 'auto';
    container.style.display = 'flex';
    container.append(...wrapper.childNodes);

    intersector.style.flexShrink = '0';
    intersector.style.width = '1px';

    container.append(intersector);
    wrapper.append(container, shadow);
    // Убираем тень если прокручен до конца
    const observer = new IntersectionObserver(([entry]) => {
      requestAnimationFrame(() => {
        shadow.style.opacity = entry.isIntersecting ? '0' : '1';
      });
    }, {
      root: container,
      threshold: 0.5,
      rootMargin: '0px',
    });

    observer.observe(intersector);
  });
}
